/**
 * standDetayService.js — Standlar > Stand Detayı (denetçi/staff tarafı)
 *
 * CANLI, mock katmanı yok (FirmaKartlarPage/firmaFuarKartService ile aynı karar).
 * Sayfa: pages/Management/Modules/Standlar/StandDetayPage.jsx
 * Arka plan: util/knowledgebase/stand-detay-sayfasi.md
 *
 * Neden standOverviewService.getStandOverviewDetail DEĞİL: o fonksiyon Proje/Firma
 * sayfalarına göre şekillendirilmiş — `notes[]` ve `jobs[]`'u atıyor, fotoğrafları
 * {id,caption,uploadedAt}'e indiriyor, borcDurumu'ndan yalnız `hasDebt`'i tutuyor ve
 * X-user-id'yi 18'e sabitliyor. Bu sayfa bunların hepsini gösteriyor.
 *
 * Okumalar (hepsi tek stand):
 *   GET /standTalep/detay/{standId}      künye, stand_detail, firms[], jobs[], photos[], notes[]
 *   GET /standTalep/list (arama+eşleme)  pil alanları (getStandRosterRow — /detay taşımıyor)
 *   GET /standTalep/borcDurumu/{standId} stand_borc kategorileri + legacy bayraklar
 * Yazmalar (staff):
 *   POST   /standTalep/updateDetail/{standId}        stand_detail — ⚠️ TAM ÜZERİNE YAZMA
 *   PUT    /standTalep/borc/{standId}/{categoryKey}  stand_borc upsert
 *   DELETE /standTalep/deleteFirmaFromStand/{relationId}
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { getStandRosterRow } from './standOverviewService';
import i18n from "../i18n";

const ST = `${JAVA_BACKEND_API_URL}/standTalep`;

// GenericResultJson başarısızlığı HTTP 200 + result:false olarak gelir (FE-README).
function assertResult(res, fallback) {
  if (res?.data && res.data.result === false) throw new Error(res.data.message || fallback);
  return res?.data;
}

function userMessage(error, fallback) {
  const d = error?.response?.data;
  if (typeof d === 'string' && d.trim()) return d;
  return d?.message || error?.message || fallback;
}

// ─── stand_detail ───────────────────────────────────────────────────────────
// `key` = updateDetail gövdesinin camelCase adı, `col` = /detay'ın snake_case kolonu
// (adlar birebir değil, eşleme burada TEK yerde). `bad: true` → açıkken sorun sayılır.
export const STAND_FLAGS = [
  { key: 'isProblem',      col: 'stand_detail_is_problem',       get label() { return i18n.t("services:standDetayService.sorunluStand"); },    bad: true },
  { key: 'isForbidden',    col: 'stand_is_forbidden',            get label() { return i18n.t("services:standDetayService.yasakliStand"); },    bad: true },
  { key: 'icTesisatOnayi', col: 'stand_detail_ic_tesisat_onayi', get label() { return i18n.t("services:standDetayService.icTesisatOnayi"); } },
  { key: 'islakImza',      col: 'stand_islak_imza',              get label() { return i18n.t("services:standDetayService.islakImza"); } },
  { key: 'internetTalebi', col: 'stand_detail_internet_talebi',  get label() { return i18n.t("services:standDetayService.internetTalebi"); } },
  { key: 'camKullanimi',   col: 'stand_detail_cam_kullanimi',    get label() { return i18n.t("services:standDetayService.camKullanimi"); } },
];

// Eski (stand_detail) borç işaretleri — stand_borc'tan AYRI bir sistem, liste
// satırının `hasDebt`'i bunlardan türüyor. ⚠️ internetBorcu okunuyor ama updateDetail'in
// alanı YOK — yazılamaz, bu yüzden listede değil (salt-okunur ayrıca döndürülüyor).
export const STAND_DEBT_FLAGS = [
  { key: 'elektrikBorcu', col: 'stand_detail_elektrik_borcu', get label() { return i18n.t("services:standDetayService.elektrik"); } },
  { key: 'suBorcu',       col: 'stand_detail_su_borcu',       get label() { return i18n.t("services:standDetayService.su"); } },
  { key: 'havaBorcu',     col: 'stand_detail_hava_borcu',     get label() { return i18n.t("services:standDetayService.hava"); } },
  { key: 'alanBorcu',     col: 'stand_detail_alan_borcu',     get label() { return i18n.t("services:standDetayService.alan"); } },
  { key: 'projeBorcu',    col: 'stand_detail_proje_borcu',    get label() { return i18n.t("services:standDetayService.proje"); } },
];

// Metin alanları — değerler kirli ("30+SU+EVYE", "25+15"): SAYIYA ÇEVİRME.
export const STAND_TEXT_FIELDS = [
  { key: 'kw',            col: 'stand_detail_kw',             label: 'kW' },
  { key: 'elektrikTalebi', col: 'stand_detail_elektrik_talebi', get label() { return i18n.t("services:standDetayService.elektrikTalebi"); } },
  { key: 'suTalebi',      col: 'stand_detail_su_talebi',      get label() { return i18n.t("services:standDetayService.suTalebi"); } },
  { key: 'havaTalebi',    col: 'stand_detail_hava_talebi',    get label() { return i18n.t("services:standDetayService.havaTalebi"); } },
  { key: 'armaturSayisi', col: 'stand_detail_armatur_sayisi', get label() { return i18n.t("services:standDetayService.armaturSayisi"); } },
  { key: 'prizSayisi',    col: 'stand_detail_priz_sayisi',    get label() { return i18n.t("services:standDetayService.prizSayisi"); } },
  { key: 'kabloKesiti',   col: 'stand_detail_kablo_kesiti',   get label() { return i18n.t("services:standDetayService.kabloKesiti"); } },
];

const WRITABLE = [...STAND_FLAGS, ...STAND_DEBT_FLAGS];

function normalizeDetail(detail) {
  const d = detail || {};
  const values = {};
  WRITABLE.forEach((f) => { values[f.key] = Number(d[f.col]) === 1; });
  STAND_TEXT_FIELDS.forEach((f) => { values[f.key] = d[f.col] ?? ''; });
  return values;
}

// ─── Fotoğraflar ────────────────────────────────────────────────────────────
// Dosyayı bu backend SERVİS ETMİYOR: legacy PHP host `/repo/standfotos/{dosya}`'dan
// veriyor (FE-StandTalepController-guide.md). Host env'de yoksa URL null → sayfa kart
// gösterir, kırık <img> değil.
const LEGACY_HOST = (process.env.REACT_APP_LEGACY_PHP_URL || '').replace(/\/$/, '');
const PHOTO_TYPE_LABELS = { get 12() { return i18n.t("services:standDetayService.elektrik_2"); }, get 15() { return i18n.t("services:standDetayService.isg"); } };

/**
 * Backlog #405 (2026-10-13): Java'dan servis edilen bir stand fotoğrafını oturum başlıklarıyla okur ve
 * object URL döndürür (GET /betterStands/{fuarId}/{dosya} artık yalnız personele açık). Çağıran yer
 * işi bitince URL.revokeObjectURL ile serbest bırakır. Okunamazsa hata fırlatır.
 */
export async function fetchStandFotografi(url) {
  const response = await axios.get(url, { headers: getAuthHeader(), responseType: 'blob' });
  return URL.createObjectURL(response.data);
}

/**
 * Tek bir standın detay sayfası için her şey — üç okuma paralel.
 * @returns {Promise<object>} normalize edilmiş stand
 */
export async function getStandDetay(standId) {
  let detayRes;
  let borcRes;
  let livePhotosRes;
  try {
    [detayRes, borcRes, livePhotosRes] = await Promise.all([
      axios.get(`${ST}/detay/${standId}`, { headers: getAuthHeader() }),
      axios.get(`${ST}/borcDurumu/${standId}`, { headers: getAuthHeader() }).catch(() => null),
      // Backlog #71/#72(4): /detay'ın photos[].url'i LEGACY_HOST tanımsız olduğu için hep null
      // (yukarıdaki not) — aynı stand_photo satırlarını Java-servis edilebilir gerçek URL'lerle
      // döndüren bu uçla, id üzerinden birleştirip düzeltiyoruz. Düşerse sessizce eski davranışa
      // (kırık değil, sadece placeholder kart) döner.
      axios.get(`${JAVA_BACKEND_API_URL}/betterStands/photos/${standId}`, { headers: getAuthHeader() }).catch(() => null),
    ]);
  } catch (error) {
    if (error?.response?.status === 404) throw new Error(i18n.t("services:standDetayService.standBulunamadi"));
    throw new Error(userMessage(error, i18n.t("services:standDetayService.standBilgileriAlinamadi")));
  }
  const d = detayRes.data;
  // Pil alanları /detay'da yok; roster satırı isimle aranıp standId ile eşleniyor.
  // Bulunamazsa null — sayfa pilleri gizler, uydurmaz.
  const roster = await getStandRosterRow(standId, { fuarId: d.fuarId, standName: d.standName });

  const firms = (d.firms || []).map((f) => ({
    relationId: f.relation_id,
    level: f.relation_level,
    firmaId: f.firma_id,
    firmaName: f.firma_name,
    firmaShortName: f.firma_shortname || null,
    firmaTypeId: f.firma_type_id,
    // type 14 (Güvenlik) için ad null geliyor — id'ye düş, boş çip basma.
    firmaTypeName: f.firma_type_name || i18n.t("services:standDetayService.tur", { firma_type_id: f.firma_type_id }),
    forbidden: Number(f.firma_is_forbidden) === 1,
  }));
  const katilimci = firms.find((f) => f.firmaTypeId === 1);
  const dekorator = firms.find((f) => f.firmaTypeId === 3 && f.level === 1);
  const detail = d.detail;
  const borc = borcRes?.data || null;
  const legacyDebt = !!detail && [...STAND_DEBT_FLAGS.map((f) => f.col), 'stand_detail_internet_borcu']
    .some((col) => Number(detail[col]) === 1);

  return {
    ...(roster || {}),
    standId: d.standId,
    standName: d.standName,
    standCode: roster?.standCode ?? null,
    fuarId: d.fuarId,
    salonId: d.salonId ?? null,
    salonName: d.salonName ?? null,
    tabelaAdi: roster?.tabelaAdi ?? null,
    firms,
    firmaId: katilimci?.firmaId ?? null,
    firmaName: katilimci?.firmaName ?? null,
    firmaShortName: katilimci?.firmaShortName ?? roster?.firmaShortName ?? null,
    dekoratorFirmaId: dekorator?.firmaId ?? null,
    dekoratorFirmaName: dekorator?.firmaName ?? null,
    hasDetailRow: !!detail,
    detailValues: normalizeDetail(detail),
    internetBorcu: Number(detail?.stand_detail_internet_borcu) === 1,
    forbiddenNote: detail?.stand_forbidden_note || null,
    // stand_detail_height cm; 0 = girilmemiş (legacy).
    detailHeightCm: Number(detail?.stand_detail_height) > 0 ? Number(detail.stand_detail_height) : null,
    standArea: roster?.standArea ?? (
      d.standDimensions && Number.isFinite(Number(d.standDimensions)) ? Number(d.standDimensions) : null
    ),
    borc: {
      categories: borc?.categories || [],
      // hasIssue için: yeni sistem VEYA eski bayrak — liste satırı eskisini okuyor.
      hasDebt: !!borc?.hasDebt,
    },
    legacyDebt,
    hasDebt: !!borc?.hasDebt || legacyDebt,
    photos: (() => {
      const liveById = new Map((livePhotosRes?.data || []).map((lp) => [lp.id, lp.url]));
      return (d.photos || []).map((p) => {
        const liveUrl = liveById.get(p.id);
        return {
          id: p.id,
          name: p.standPhotoName || i18n.t("services:standDetayService.fotograf"),
          typeLabel: PHOTO_TYPE_LABELS[p.fileTypeId] || null,
          date: p.standPhotoInsertdate || null,
          url: liveUrl
            ? `${JAVA_BACKEND_API_URL.replace(/\/api\/v1$/, '')}${liveUrl}`
            : (LEGACY_HOST && p.standPhoto ? `${LEGACY_HOST}/repo/standfotos/${p.standPhoto}` : null),
          // Backlog #405: Java'nın fotoğraf ucu artık oturum ister (yalnız personel). <img src> başlık
          // gönderemez; bu bayrak true ise sayfa baytları fetchStandFotografi ile (blob) okur.
          korumali: !!liveUrl,
        };
      });
    })(),
    notes: (d.notes || []).map((n) => ({
      id: n.id, text: n.stand_note_text, date: n.stand_note_insertdate, primary: Number(n.stand_note_is_primary) === 1,
    })),
    jobs: (d.jobs || []).map((j) => ({
      id: j.stand_job_id,
      typeName: j.stand_job_type_name || i18n.t("services:standDetayService.is"),
      status: j.stand_job_status_id,
      worker: [j.worker_name, j.worker_surname].filter(Boolean).join(' ') || null,
      auditor: [j.auditor_name, j.auditor_surname].filter(Boolean).join(' ') || null,
      start: j.stand_job_start_datetime,
      end: j.stand_job_end_datetime,
    })),
  };
}

/**
 * stand_detail'i yazar. ⚠️ TAM ÜZERİNE YAZMA: gönderilmeyen her alan false/null olur,
 * bu yüzden HER ZAMAN tüm değerler (getStandDetay'ın detailValues'u + değişiklik)
 * gönderilir. Yan etki: elektrikBorcu:false elektrik fişini "yazdırılabilir" işaretler
 * — tüm gövde mevcut değerden geldiği için yalnızca kullanıcı gerçekten kapatınca olur.
 */
export async function updateStandDetail(standId, values) {
  const body = {};
  WRITABLE.forEach((f) => { body[f.key] = !!values[f.key]; });
  STAND_TEXT_FIELDS.forEach((f) => {
    const v = values[f.key];
    body[f.key] = v === '' || v == null ? null : String(v);
  });
  try {
    const res = await axios.post(`${ST}/updateDetail/${standId}`, body, { headers: getAuthHeader() });
    return assertResult(res, 'Stand durumu kaydedilemedi.');
  } catch (error) {
    throw new Error(userMessage(error, 'Stand durumu kaydedilemedi.'));
  }
}

/**
 * stand_borc kategorisini günceller. amount yalnızca "bekliyor"da anlamlı; boş bırakılabilir.
 * @returns güncel { categories, hasDebt, ... }
 */
export async function setStandBorc(standId, categoryKey, { status, amount }) {
  const body = { status };
  if (status === 'bekliyor' && amount !== '' && amount != null) body.amount = Number(amount);
  try {
    const res = await axios.put(`${ST}/borc/${standId}/${categoryKey}`, body, { headers: getAuthHeader() });
    return assertResult(res, i18n.t("services:standDetayService.borcGuncellenemedi"))?.data;
  } catch (error) {
    throw new Error(userMessage(error, i18n.t("services:standDetayService.borcGuncellenemedi")));
  }
}

// WAS (until #324): /** firma_to_stand satırını siler. Yalnız SuperAdmin/Admin (grup 1/2) — diğerleri 403. */
/** firma_to_stand satırını siler. Personel: stand.firma.manage yetkisi (#324, V77: Admin; kök her zaman); diğerleri 403. */
export async function removeFirmaFromStand(relationId) {
  try {
    const res = await axios.delete(`${ST}/deleteFirmaFromStand/${relationId}`, { headers: getAuthHeader() });
    return assertResult(res, i18n.t("services:standDetayService.firmaStanddanKaldirilamadi"));
  } catch (error) {
    if (error?.response?.status === 403) throw new Error(i18n.t("services:standDetayService.buIslemIcinYetkinizYok"));
    throw new Error(userMessage(error, i18n.t("services:standDetayService.firmaStanddanKaldirilamadi")));
  }
}
